<template>
  <div
    ref="rootEl"
    class="relative"
  >
    <button
      ref="buttonEl"
      type="button"
      class="relative -m-2.5 p-2.5 mt-1 text-gray-400 hover:text-gray-500"
      @click.stop="toggleMenu"
    >
      <span class="sr-only">View notifications</span>
      <BellIcon class="h-8 w-8" />
      <span
        v-if="Number(unreadCount || 0) > 0"
        class="absolute right-1 top-1 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[11px] font-semibold leading-none text-white shadow"
      >
        {{ unreadCountLabel }}
      </span>
    </button>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="isOpen"
          class="fixed inset-0 z-[120]"
          @click="closeMenu"
        >
          <div
            class="absolute w-[520px] max-w-[calc(100vw-1rem)] rounded-xl border border-zinc-200 bg-white shadow-2xl bg-white/70 backdrop-blur-sm"
            :style="menuStyle"
            @click.stop
          >
            <div class="flex items-center justify-between border-b border-zinc-300 px-4 py-3">
              <h3 class="text-lg font-semibold text-zinc-800">
                Notifications
              </h3>
              <button
                type="button"
                class="rounded-md bg-[#2D8CFF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
                :disabled="localUnreadCount < 1 || isWorking"
                @click="markVisibleAsRead"
              >
                Mark {{ Math.min(localUnreadCount, notifications.length) }} as read
              </button>
            </div>

            <div class="max-h-[420px] overflow-y-auto app-scroll">
              <div
                v-if="loading"
                class="px-4 py-4 text-sm text-zinc-400"
              >
                Loading notifications...
              </div>
              <div
                v-else-if="notifications.length === 0"
                class="px-4 py-6 text-sm text-zinc-500"
              >
                No notifications yet.
              </div>
              <ul
                v-else
                class="divide-y divide-zinc-300"
              >
                <li
                  v-for="item in notifications"
                  :key="item.notificationSlug"
                  class="relative cursor-pointer px-4 py-3 hover:bg-zinc-50"
                  @click="goToNotification(item)"
                >
                  <div class="flex items-start gap-3">
                    <span
                      class="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                      :class="item.isRead ? 'bg-transparent' : 'bg-[#2D8CFF]'"
                    />
                    <div class="relative inline-flex shrink-0">
                      <img
                        :src="getAuthorAvatar(item)"
                        alt="Author avatar"
                        :class="getNotificationAvatarClass(item)"
                        @error="onAvatarError"
                      >
                      <img
                        v-if="getReactionIcon(item)"
                        :src="getReactionIcon(item)"
                        alt="Reaction"
                        class="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-white ring-1 ring-white"
                      >
                    </div>
                    <div class="min-w-0 flex-1">
                      <p
                        class="truncate text-sm"
                        :class="[item.isRead ? 'font-normal' : 'font-semibold', getNotificationDisplayClass(item)]"
                      >
                        {{ getNotificationDisplayText(item) }}
                      </p>
                      <p class="mt-1 text-xs text-zinc-500">
                        {{ formatDate(item.createdAt) }}
                      </p>
                    </div>
                    <button
                      type="button"
                      class="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                      @click.stop="toggleActions(item.notificationSlug)"
                    >
                      <EllipsisHorizontalIcon class="h-5 w-5" />
                    </button>
                  </div>

                  <div
                    v-if="openActionsSlug === item.notificationSlug"
                    class="absolute right-8 top-10 z-20 w-40 rounded-lg border border-zinc-200 bg-white py-1 shadow-xl"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="block w-full px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50"
                      @click="goToNotification(item)"
                    >
                      See details
                    </button>
                    <button
                      v-if="!item.isRead"
                      type="button"
                      class="block w-full px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50"
                      @click="markOneAsRead(item)"
                    >
                      Mark as read
                    </button>
                  </div>
                </li>
              </ul>
            </div>

            <div class="flex items-center justify-between border-t border-zinc-300 px-4 py-3 text-sm">
              <span class="text-zinc-600">
                {{ notifications.length }} shown, {{ localUnreadCount }} unread
              </span>
              <button
                type="button"
                class="font-semibold text-[#2D8CFF] hover:underline"
                @click="goToAllNotifications"
              >
                View all
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { BellIcon, EllipsisHorizontalIcon } from '@heroicons/vue/24/outline';
import defaultAvatar from '@/assets/media/images/default-avatar.png';
const btcIcon = new URL('@/assets/media/images/btc_icon_Platforma_Icons.svg', import.meta.url).href;
const likeIcon = new URL('@/assets/media/images/newsfeed-like-icon.png', import.meta.url).href;
const loveIcon = new URL('@/assets/media/images/newsfeed-love-icon.png', import.meta.url).href;
const careIcon = new URL('@/assets/media/images/newsfeed-care-icon.png', import.meta.url).href;
const hahaIcon = new URL('@/assets/media/images/newsfeed-haha-icon.png', import.meta.url).href;
const wowIcon = new URL('@/assets/media/images/newsfeed-wow-icon.png', import.meta.url).href;
const sadIcon = new URL('@/assets/media/images/newsfeed-sad-icon.png', import.meta.url).href;
const angryIcon = new URL('@/assets/media/images/newsfeed-angry-icon.png', import.meta.url).href;
const commentIcon = new URL('@/assets/media/images/comment-platforma-newsfeed-icon.png', import.meta.url).href;
const postIcon = new URL('@/assets/media/images/post-platforma-newsfeed-icon.png', import.meta.url).href;

defineProps({
  unreadCount: {
    type: Number,
    default: 0,
  },
  unreadCountLabel: {
    type: String,
    default: '0',
  },
});

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL || '';
const router = useRouter();
const route = useRoute();
const isOpen = ref(false);
const isWorking = ref(false);
const loading = ref(false);
const notifications = ref([]);
const unreadCountFromApi = ref(0);
const rootEl = ref(null);
const buttonEl = ref(null);
const menuPosition = ref({ top: 56, left: 16 });
const openActionsSlug = ref('');

const localUnreadCount = computed(() => {
  const fromList = notifications.value.filter((n) => !n.isRead).length;
  return Math.max(Number(unreadCountFromApi.value || 0), fromList);
});

const menuStyle = computed(() => ({
  top: `${menuPosition.value.top}px`,
  left: `${menuPosition.value.left}px`,
}));

function dispatchUnreadSync() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('notifications:unread-sync'));
}

function parseNotificationPayload(notification) {
  const raw =
    notification?.payload ??
    notification?.payloadJson ??
    notification?.payload_json ??
    notification?.metadata ??
    notification?.meta;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  let current = String(raw);
  for (let index = 0; index < 3; index += 1) {
    if (current && typeof current === 'object') return current;
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) break;
    try {
      current = JSON.parse(trimmed);
    } catch {
      break;
    }
  }
  return {};
}

function isBtcAvatarSrc(value) {
  const src = String(value || '').trim();
  return src.includes('btc_icon_Platforma_Icons.svg');
}

function normalizeAvatarSrc(value, featureInstanceId) {
  const raw = String(value || '').trim();
  if (!raw) return defaultAvatar;
  const match = raw.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (!match?.[1]) return raw;

  const params = new URLSearchParams();
  const iid = Number(featureInstanceId || 0);
  if (Number.isFinite(iid) && iid > 0) {
    params.set('featureInstanceId', String(iid));
  }
  const qs = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${match[1]}${qs ? `?${qs}` : ''}`;
}

function onAvatarError(event) {
  const img = event?.target;
  if (!img || img.tagName !== 'IMG') return;
  img.src = defaultAvatar;
}

function resolveVideoPublishEventType(notification, payload = {}) {
  const eventType = String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.category ||
      payload?.source ||
      payload?.source_type ||
      ''
  ).toLowerCase().trim();
  if (eventType === 'video_channel_video_published' || eventType === 'video-channel-video-published' || eventType.includes('video_channel') || eventType.includes('video-channel')) return 'video-channel';
  if (eventType === 'flix_theatre_video_published' || eventType === 'flix-theatre-video-published' || eventType.includes('flix_theatre') || eventType.includes('flix-theatre')) return 'flix-theatre';
  const hasFlixHints = Boolean(
      payload?.theatreLogoMediaFileId ||
      payload?.theatre_logo_media_file_id ||
      payload?.theatreAvatarMediaFileId ||
      payload?.theatre_avatar_media_file_id ||
      payload?.theatreLogoUrl ||
      payload?.theatre_logo_url ||
      payload?.theatreAvatarUrl ||
      payload?.theatre_avatar_url ||
      payload?.theatreTitle ||
      payload?.theatre_title ||
      payload?.flixTitle ||
      payload?.flix_title
  );
  if (hasFlixHints) return 'flix-theatre';
  const text = String(
    notification?.title ||
      notification?.body ||
      payload?.title ||
      payload?.body ||
      ''
  ).toLowerCase().trim();
  if (text.includes('flix theatre') || (text.includes('streaming release') && text.includes('flix'))) {
    return 'flix-theatre';
  }
  return '';
}

function isVideoPublishNotification(notification, payload = {}) {
  return !!resolveVideoPublishEventType(notification, payload);
}
function isFlixVideoPublishNotification(notification, payload = {}) {
  return resolveVideoPublishEventType(notification, payload) === 'flix-theatre';
}
function resolveStorefrontNotificationEventType(notification, payload = {}) {
  const eventType = String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.source ||
      ''
  ).toLowerCase().trim();
  if (eventType.startsWith('storefront_')) return eventType;
  return '';
}
function isStorefrontNotification(notification, payload = {}) {
  return !!resolveStorefrontNotificationEventType(notification, payload);
}
function resolveNotificationEventType(notification, payload = {}) {
  return String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.category ||
      payload?.source ||
      payload?.source_type ||
      ''
  ).toLowerCase().trim();
}
function isCreatorMemberSubscriptionPaymentFailedNotification(notification, payload = {}) {
  const eventType = resolveNotificationEventType(notification, payload);
  return (
    eventType === 'member_subscription_payment_failed' ||
    eventType === 'member.subscription_payment_failed' ||
    eventType === 'subscription_payment_failed'
  );
}
function isCryptoReceiveNotification(notification, payload = {}) {
  const eventType = resolveNotificationEventType(notification, payload);
  return eventType === 'crypto_btc_received';
}
function resolveStorefrontMemberAvatar(notification, payload = {}) {
  const raw = String(
    payload?.memberAvatarUrl ||
      notification?.memberAvatarUrl ||
      payload?.member_avatar_url ||
      notification?.member_avatar_url ||
      payload?.avatarUrl ||
      notification?.avatarUrl ||
      payload?.avatar_url ||
      notification?.avatar_url ||
      ''
  ).trim();
  if (!raw) return '';
  return normalizeAvatarSrc(
    raw,
    notification?.featureInstanceId || notification?.feature_instance_id
  );
}
function buildStorefrontOrderDisplayText(notification, payload = {}) {
  const storefrontName = String(
    payload?.storefrontName ||
      payload?.storefront_name ||
      payload?.storeName ||
      payload?.store_name ||
      payload?.featureInstanceName ||
      payload?.instanceName ||
      payload?.storefrontTitle ||
      'Storefront'
  ).trim() || 'Storefront';
  const memberName = String(
    payload?.memberName || payload?.member_name || notification?.title || 'Member'
  ).trim() || 'Member';
  const orderNumber = String(
    payload?.orderNumber || payload?.order_number || notification?.orderNumber || ''
  ).trim();
  if (orderNumber) {
    return `${storefrontName} Order #${orderNumber} placed by ${memberName}`;
  }
  return `${storefrontName} Order placed by ${memberName}`;
}

function resolveCreatorVideoPublishAvatar(notification, payload = {}) {
  const eventType = resolveVideoPublishEventType(notification, payload);
  if (!eventType) return '';
  const featureInstanceId = Number(
    notification?.featureInstanceId ||
      notification?.feature_instance_id ||
      payload?.featureInstanceId ||
      payload?.feature_instance_id ||
      payload?.instanceId ||
      payload?.instance_id ||
      payload?.feature?.instanceId ||
      payload?.feature?.instance_id ||
      0
  );
  if (eventType === 'flix-theatre') {
    const logoFileId = String(
      payload?.creatorLogoMediaFileId ||
        notification?.creatorLogoMediaFileId ||
        payload?.creator_logo_media_file_id ||
        notification?.creator_logo_media_file_id ||
        payload?.theatreLogoMediaFileId ||
        notification?.theatreLogoMediaFileId ||
        payload?.theatre_logo_media_file_id ||
        notification?.theatre_logo_media_file_id ||
        payload?.theatreAvatarMediaFileId ||
        notification?.theatreAvatarMediaFileId ||
        payload?.theatre_avatar_media_file_id ||
        notification?.theatre_avatar_media_file_id ||
        payload?.channelAvatarMediaFileId ||
        notification?.channelAvatarMediaFileId ||
        payload?.channel_avatar_media_file_id ||
        notification?.channel_avatar_media_file_id ||
        ''
    ).trim();
    if (logoFileId && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
      const qs = new URLSearchParams({
        featureInstanceId: String(Math.trunc(featureInstanceId)),
      }).toString();
      return `${apiProxyUrl}/api/creator-flix-theatre/image/${encodeURIComponent(logoFileId)}?${qs}`;
    }
    const directLogo = String(
      payload?.creatorLogoUrl ||
        notification?.creatorLogoUrl ||
        payload?.creator_logo_url ||
        notification?.creator_logo_url ||
        payload?.theatreLogoUrl ||
        notification?.theatreLogoUrl ||
        payload?.theatre_logo_url ||
        notification?.theatre_logo_url ||
        payload?.creatorAvatarUrl ||
        notification?.creatorAvatarUrl ||
        payload?.creator_avatar_url ||
        notification?.creator_avatar_url ||
        payload?.avatarUrl ||
        notification?.avatarUrl ||
        payload?.avatar_url ||
        notification?.avatar_url ||
        payload?.authorAvatarUrl ||
        notification?.authorAvatarUrl ||
        payload?.author_avatar_url ||
        notification?.author_avatar_url ||
        payload?.authorAvatar ||
        notification?.authorAvatar ||
        payload?.channelAvatarUrl ||
        notification?.channelAvatarUrl ||
        payload?.channel_avatar_url ||
        notification?.channel_avatar_url ||
        payload?.theatreAvatarUrl ||
        notification?.theatreAvatarUrl ||
        payload?.theatre_avatar_url ||
        notification?.theatre_avatar_url ||
        payload?.logoUrl ||
        notification?.logoUrl ||
        payload?.logo_url ||
        notification?.logo_url ||
        ''
    ).trim();
    if (directLogo) return directLogo;
    return `${apiProxyUrl}/api/main-app-logo`;
  }
  const fileId = String(
    payload?.channelAvatarMediaFileId ||
      payload?.channel_avatar_media_file_id ||
      payload?.theatreAvatarMediaFileId ||
      payload?.theatre_avatar_media_file_id ||
      ''
  ).trim();
  if (fileId && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(Math.trunc(featureInstanceId)),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    return `${apiProxyUrl}${imagePrefix}${encodeURIComponent(fileId)}?${qs}`;
  }

  const direct = String(
    payload?.creatorAvatarUrl ||
      payload?.creator_avatar_url ||
      payload?.theatreAvatarUrl ||
      payload?.theatre_avatar_url ||
      payload?.channelAvatarUrl ||
      payload?.channel_avatar_url ||
      payload?.authorAvatar ||
      payload?.authorAvatarUrl ||
      payload?.author_avatar_url ||
      payload?.avatarUrl ||
      payload?.avatar_url ||
      ''
  ).trim();
  const match = direct.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (match?.[1] && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(Math.trunc(featureInstanceId)),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    return `${apiProxyUrl}${imagePrefix}${encodeURIComponent(match[1])}?${qs}`;
  }
  return direct || '';
}

function getAuthorAvatar(notification) {
  const payload = parseNotificationPayload(notification);
  if (isCryptoReceiveNotification(notification, payload)) return btcIcon;
  if (isStorefrontNotification(notification, payload)) {
    const memberAvatar = resolveStorefrontMemberAvatar(notification, payload);
    if (memberAvatar) return memberAvatar;
  }
  if (isVideoPublishNotification(notification, payload)) {
    const direct = resolveCreatorVideoPublishAvatar(notification, payload);
    if (direct) return direct;
  }
  const raw = String(
    payload?.authorAvatar ||
      payload?.authorAvatarUrl ||
      payload?.author_avatar_url ||
      payload?.avatarUrl ||
      payload?.avatar_url ||
      notification?.authorAvatar ||
      notification?.authorAvatarUrl ||
      notification?.author_avatar_url ||
      notification?.avatarUrl ||
      notification?.avatar_url ||
      ''
  ).trim();
  return normalizeAvatarSrc(
    raw,
    notification?.featureInstanceId || notification?.feature_instance_id
  );
}
function getNotificationAvatarClass(notification) {
  const payload = parseNotificationPayload(notification);
  const avatarSrc = getAuthorAvatar(notification);
  if (isCryptoReceiveNotification(notification, payload) || isBtcAvatarSrc(avatarSrc)) {
    return 'h-8 w-8 rounded-full object-cover scale-[1.12]';
  }
  if (isStorefrontNotification(notification, payload)) {
    return 'h-8 w-8 rounded-full object-cover ring-1 ring-zinc-200';
  }
  if (isFlixVideoPublishNotification(notification, payload)) {
    return 'h-8 w-auto max-w-[96px] shrink-0 object-contain';
  }
  return 'h-8 w-8 rounded-full object-cover ring-1 ring-zinc-200';
}
function getNotificationDisplayText(notification) {
  const payload = parseNotificationPayload(notification);
  if (isStorefrontNotification(notification, payload)) {
    return buildStorefrontOrderDisplayText(notification, payload);
  }
  if (isFlixVideoPublishNotification(notification, payload)) {
    const featureName = String(
      payload?.theatreTitle || notification?.title || 'Flix Theatre'
    ).trim() || 'Flix Theatre';
    return `New Streaming Release in ${featureName}`;
  }
  const title = String(notification?.title || '').trim();
  const body = String(notification?.body || '').trim();
  return [title, body].filter(Boolean).join(' ');
}
function getNotificationDisplayClass(notification) {
  const payload = parseNotificationPayload(notification);
  if (isCreatorMemberSubscriptionPaymentFailedNotification(notification, payload)) {
    return notification?.isRead ? 'text-red-500' : 'text-red-600';
  }
  return 'text-zinc-800';
}

function getReactionIcon(notification) {
  const payload = parseNotificationPayload(notification);
  if (isCryptoReceiveNotification(notification, payload)) return '';
  if (isStorefrontNotification(notification, payload)) return '';
  const eventType = String(
    notification?.eventType || payload?.eventType || payload?.source || ''
  ).toLowerCase().trim();
  const type = String(payload?.reactionType || '').toLowerCase().trim();
  if (type === 'like') return likeIcon;
  if (type === 'love') return loveIcon;
  if (type === 'care') return careIcon;
  if (type === 'haha') return hahaIcon;
  if (type === 'wow') return wowIcon;
  if (type === 'sad') return sadIcon;
  if (type === 'angry') return angryIcon;
  if (
    eventType === 'newsfeed_post_created'
  ) {
    return postIcon;
  }
  if (
    eventType === 'newsfeed_comment_created' ||
    eventType === 'newsfeed_comment_on_your_post' ||
    eventType === 'newsfeed_reply_created' ||
    eventType === 'newsfeed_reply_on_your_comment'
  ) {
    return commentIcon;
  }
  return '';
}

function formatDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function updateMenuPosition() {
  if (!buttonEl.value) return;
  const rect = buttonEl.value.getBoundingClientRect();
  const width = Math.min(520, window.innerWidth - 16);
  const left = Math.max(8, Math.min(window.innerWidth - width - 8, rect.right - width));
  const top = Math.max(8, rect.bottom + 8);
  menuPosition.value = { top, left };
}

async function fetchNotifications() {
  loading.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/notifications?limit=8&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    notifications.value = Array.isArray(data?.notifications) ? data.notifications : [];
    unreadCountFromApi.value = Number(data?.unreadCount || 0);
  } catch (error) {
    console.error('[NotificationBellMenuCreator] fetchNotifications error:', error);
  } finally {
    loading.value = false;
  }
}

async function markOneAsRead(item) {
  if (!item?.notificationSlug || item.isRead) return;
  isWorking.value = true;
  try {
    await fetch(`${apiProxyUrl}/api/notifications/${encodeURIComponent(item.notificationSlug)}/read`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    item.isRead = 1;
    dispatchUnreadSync();
    openActionsSlug.value = '';
  } catch (error) {
    console.error('[NotificationBellMenuCreator] markOneAsRead error:', error);
  } finally {
    isWorking.value = false;
  }
}

async function markVisibleAsRead() {
  const unreadItems = notifications.value.filter((n) => !n.isRead);
  if (!unreadItems.length) return;
  isWorking.value = true;
  try {
    for (const item of unreadItems) {
      await fetch(`${apiProxyUrl}/api/notifications/${encodeURIComponent(item.notificationSlug)}/read`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      item.isRead = 1;
    }
    dispatchUnreadSync();
  } catch (error) {
    console.error('[NotificationBellMenuCreator] markVisibleAsRead error:', error);
  } finally {
    isWorking.value = false;
  }
}

async function markOpened(item) {
  if (!item?.notificationSlug) return;
  try {
    await fetch(`${apiProxyUrl}/api/notifications/${encodeURIComponent(item.notificationSlug)}/opened`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (error) {
    console.error('[NotificationBellMenuCreator] markOpened error:', error);
  }
}

async function goToNotification(item) {
  if (!item?.notificationSlug) return;
  await markOpened(item);
  if (!item.isRead) {
    await markOneAsRead(item);
  }
  closeMenu();
  await router.push(`/creator/notifications/${encodeURIComponent(item.notificationSlug)}`);
}

function goToAllNotifications() {
  closeMenu();
  router.push('/creator/notifications');
}

function toggleActions(slug) {
  openActionsSlug.value = openActionsSlug.value === slug ? '' : slug;
}

function toggleMenu() {
  if (isOpen.value) {
    closeMenu();
    return;
  }
  openMenu();
}

async function openMenu() {
  isOpen.value = true;
  openActionsSlug.value = '';
  updateMenuPosition();
  await fetchNotifications();
}

function closeMenu() {
  isOpen.value = false;
  openActionsSlug.value = '';
}

function onEsc(e) {
  if (e.key === 'Escape') closeMenu();
}

function onWindowChange() {
  if (!isOpen.value) return;
  updateMenuPosition();
}

onMounted(() => {
  window.addEventListener('keydown', onEsc);
  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onEsc);
  window.removeEventListener('resize', onWindowChange);
  window.removeEventListener('scroll', onWindowChange, true);
});

watch(
  () => route.fullPath,
  () => {
    closeMenu();
  }
);
</script>

<style scoped>
/* ─────────────────────────────────────────────
   Creator layout scrollbar (right column only)
   ─────────────────────────────────────────── */

/* Firefox base */
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  /* try to keep layout width stable */
  scrollbar-gutter: stable;
}

/* WebKit base (Chrome / Edge / Safari) */
.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/* Hide old-school buttons/arrows (where they exist) */
.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

/* Track fully invisible so it looks “on top” of content */
.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

/* Thumb invisible by default */
.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

/* On hover over the scroll area: fade thumb in */
.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent; /* Firefox */
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

/* Slightly stronger when directly hovering the thumb */
.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
